/*
  LSC - Article card row carousel controller (shared)
  Used as <lsc-article-carousel> by snippets/lsc-article-row.liquid, which
  every article card row renders through (ingredient row, skin concern row,
  "Ingredients that help" row).

  LSC - 11 Oct 2026 (article rows audit, finding 4): renamed from
  assets/lsc-ingredient-carousel.js. The element is now <lsc-article-carousel>
  and the hooks are .js-lsc-arow-track / -nav / -prev / -next. Behaviour is
  unchanged. The old file is no longer loaded and can be deleted.

  What it does: shows the arrow buttons only when the card row overflows,
  scrolls one card per click, and disables each arrow at its end of the row.

  LSC - 14 Oct 2026: also sets data-fits on the track while every card fits
  without scrolling. The stylesheet centres the cards only then ("Card
  alignment: Center"). Centring an overflowing scroller would push its
  first cards out of reach, and before this script runs there is no
  attribute, so the safe left-aligned layout is the starting state.

  Why this file exists (audit findings 1 and 2, 4 Oct 2026):
  - It used to be printed inline in every section instance. As an asset it is
    downloaded once, cached, and loaded with defer so it never blocks parsing.
  - The old version added a window resize listener that was never removed.
    The theme editor re-renders the section on every setting change, so each
    change left a listener pointing at a detached element. This version
    watches the track with a ResizeObserver and tears everything down in
    disconnectedCallback.
  - The old scroll handler re-measured layout on every scroll event (67 calls
    in 20 frames when measured). Measurement is now batched into at most one
    requestAnimationFrame per frame.
*/
(function () {
  'use strict';

  // LSC - Script can be included more than once (two section instances, or a
  // theme editor re-render). Define the element only the first time.
  if (customElements.get('lsc-article-carousel')) return;

  // LSC - Honour the visitor's reduced-motion preference when paging.
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class LscArticleCarousel extends HTMLElement {
    /*
      LSC - If this element is parsed after the class is defined (a second
      instance on the page), connectedCallback fires before its children
      exist. Wait for the DOM in that case.
    */
    connectedCallback() {
      if (this._ready) return;
      if (document.readyState === 'loading') {
        this._onDomReady = () => this._init();
        document.addEventListener('DOMContentLoaded', this._onDomReady, { once: true });
      } else {
        this._init();
      }
    }

    /* LSC - Remove every listener and observer when the element leaves the page. */
    disconnectedCallback() {
      this._teardown();
    }

    /* LSC - One-time setup: find the track and controls, wire the events. */
    _init() {
      if (this._ready) return;
      this.track = this.querySelector('.js-lsc-arow-track');
      if (!this.track) return;
      this._ready = true;

      this.nav = this.querySelector('.js-lsc-arow-nav');
      this.prevBtn = this.querySelector('.js-lsc-arow-prev');
      this.nextBtn = this.querySelector('.js-lsc-arow-next');

      // LSC - Keep handler references so they can be removed in _teardown().
      this._onPrev = () => this._page(-1);
      this._onNext = () => this._page(1);
      this._onScroll = () => this._schedule();

      if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);
      this.track.addEventListener('scroll', this._onScroll, { passive: true });

      // LSC - Re-check overflow whenever the track changes size (viewport
      // resize, font load, editor setting change). Falls back to a window
      // resize listener on browsers without ResizeObserver.
      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(() => this._schedule());
        this._resizeObserver.observe(this.track);
      } else {
        this._onResize = () => this._schedule();
        window.addEventListener('resize', this._onResize);
      }

      this._sync();
    }

    /* LSC - Undo everything _init() set up. Safe to call more than once. */
    _teardown() {
      if (this._onDomReady) {
        document.removeEventListener('DOMContentLoaded', this._onDomReady);
        this._onDomReady = null;
      }
      if (!this._ready) return;
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNext);
      if (this.track) this.track.removeEventListener('scroll', this._onScroll);
      if (this._resizeObserver) this._resizeObserver.disconnect();
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      if (this._raf) cancelAnimationFrame(this._raf);
      this._resizeObserver = null;
      this._onResize = null;
      this._raf = 0;
      this._ready = false;
    }

    /* LSC - Batch layout reads: at most one _sync() per animation frame. */
    _schedule() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = 0;
        this._sync();
      });
    }

    /* LSC - Scroll by one card width plus its gap. */
    _page(direction) {
      var card = this.track.querySelector('li');
      if (!card) return;
      var gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({
        left: direction * (card.offsetWidth + gap),
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    }

    /* LSC - Hide the arrows when nothing overflows; disable each at its end. */
    _sync() {
      if (!this.track) return;
      var overflowing = this.track.scrollWidth > this.track.clientWidth + 2;
      // LSC - 14 Oct 2026: tell the stylesheet whether the cards all fit.
      this.track.toggleAttribute('data-fits', !overflowing);
      if (this.nav) this.nav.hidden = !overflowing;
      if (!overflowing) return;

      var atStart = this.track.scrollLeft <= 2;
      var atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
      if (this.prevBtn) this.prevBtn.disabled = atStart;
      if (this.nextBtn) this.nextBtn.disabled = atEnd;
    }
  }

  customElements.define('lsc-article-carousel', LscArticleCarousel);
})();
